import { useState } from 'react';
import { BTN_PRIMARY, BTN_SECONDARY, BTN_DESTRUCTIVE_OUTLINE, TEXT_MUTED, TEXT_DISABLED, BORDER_DEFAULT, TEXT_HEADING, TEXT_DEFAULT, BG_SURFACE, BRAND_PRIMARY_DARK, ERROR_TEXT, ERROR_BG, STATUS_GOOD, BG_SHELL, BG_HOVER_LT, STATUS_WARN, SURFACE_WHITE, FONT_BUTTON, FONT_SECTION_TITLE, FONT_BODY, FONT_LABEL, FONT_SMALL, FONT_TINY } from '../../theme';
import ConfirmDialog from './ConfirmDialog';

// ── I18N ──────────────────────────────────────────────────────────────────────
const T = {
  es: {
    downloadTemplate:  'Descargar plantilla',
    uploadFile:        'Seleccionar archivo CSV',
    step1Hint:         'Descarga la plantilla, rellena tus datos y sube el archivo CSV.',
    back:              'Atrás',
    importN:           'Importar {n} filas',
    summaryReady:      '{n} filas listas para importar',
    summaryErrors:     '{n} filas con errores (se omitirán)',
    summaryWarnings:   '{n} filas con advertencias',
    noValidRows:       'No hay filas válidas para importar.',
    successMsg:        '{n} registros importados correctamente.',
    done:              'Listo',
    tryAgain:          'Reintentar',
    errorImport:       'Error al importar: ',
    errRequired:       'Campo obligatorio',
    errInvalidEnum:    'Valor no reconocido',
    errNotNumber:      'Debe ser un número',
    colHeader:         'Columna',
    parseError:        'No se pudo leer el archivo CSV.',
    emptyFile:         'El archivo está vacío o no contiene datos.',
    delimiterHint:     'Formato soportado: separador coma (,) o punto y coma (;).',
    importing:         'Importando…',
    warnDuplicate:     'Valor duplicado en este archivo (se omitirá)',
    warnInvalidTimezone: 'Zona horaria no reconocida. Se recomienda formato IANA (ej. Europe/Madrid).',
    // §11 rule 38 — multi-entity write: checkpoint and rollback
    failureDialogMsg:  'Se produjo un error al escribir un registro durante la importación. {n} registros ya se guardaron correctamente en esta sesión.\n\nElige cómo continuar:',
    failureRetry:      'Reintentar y continuar',
    failureRollback:   'Deshacer este lote',
    failureKeep:       'Mantener progreso parcial',
  },
  en: {
    downloadTemplate:  'Download template',
    uploadFile:        'Select CSV file',
    step1Hint:         'Download the template, fill in your data, and upload the CSV file.',
    back:              'Back',
    importN:           'Import {n} rows',
    summaryReady:      '{n} rows ready to import',
    summaryErrors:     '{n} rows with errors (will be skipped)',
    summaryWarnings:   '{n} rows with warnings',
    noValidRows:       'No valid rows to import.',
    successMsg:        '{n} records imported successfully.',
    done:              'Done',
    tryAgain:          'Try again',
    errorImport:       'Import error: ',
    errRequired:       'Required field',
    errInvalidEnum:    'Unrecognized value',
    errNotNumber:      'Must be a number',
    colHeader:         'Column',
    parseError:        'Could not read the CSV file.',
    emptyFile:         'The file is empty or contains no data.',
    delimiterHint:     'Supported format: comma (,) or semicolon (;) separated.',
    importing:         'Importing…',
    warnDuplicate:     'Duplicate value in this file (will be skipped)',
    warnInvalidTimezone: 'Unrecognized time zone. Consider using IANA format (e.g. Europe/Madrid).',
    // §11 rule 38 — multi-entity write: checkpoint and rollback
    failureDialogMsg:  'An error occurred while writing a record during import. {n} records were already written successfully in this session.\n\nChoose how to continue:',
    failureRetry:      'Retry and continue',
    failureRollback:   'Rollback this batch',
    failureKeep:       'Keep partial progress',
  },
};

// ── CSV HELPERS ───────────────────────────────────────────────────────────────
function detectDelimiter(firstLine) {
  const semicolons = (firstLine.match(/;/g) || []).length;
  const commas     = (firstLine.match(/,/g) || []).length;
  return semicolons > commas ? ';' : ',';
}

function parseCSVLine(line, delimiter) {
  const cells = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"' && line[i + 1] === '"') { current += '"'; i++; }
      else if (ch === '"') { inQuotes = false; }
      else { current += ch; }
    } else {
      if (ch === '"') { inQuotes = true; }
      else if (ch === delimiter) { cells.push(current); current = ''; }
      else { current += ch; }
    }
  }
  cells.push(current);
  return cells.map(c => c.trim());
}

function parseCSV(text) {
  const lines = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n').filter(l => l.trim() && !l.trimStart().startsWith('#'));
  if (lines.length < 2) return { headers: [], rows: [] };
  const delimiter = detectDelimiter(lines[0]);
  const headers = parseCSVLine(lines[0], delimiter);
  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const cells = parseCSVLine(lines[i], delimiter);
    const obj = {};
    headers.forEach((h, idx) => { obj[h] = cells[idx] !== undefined ? cells[idx] : ''; });
    rows.push(obj);
  }
  return { headers, rows };
}

// ── TEMPLATE GENERATION ───────────────────────────────────────────────────────
function generateTemplate(columns, filename) {
  const headers = columns.map(c => c.label);
  const csv = headers.join(',') + '\n';
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ── VALIDATION ────────────────────────────────────────────────────────────────
function validateRows(rawRows, columns, t, dedupeKey) {
  const seenDedupeValues = new Set();
  return rawRows.map(raw => {
    const parsed = {};
    const errors = {};   // red — blocks import
    const warnings = {}; // amber — import proceeds

    columns.forEach(col => {
      let val = null;
      // Find matching header (by label or key)
      const rawVal = raw[col.label] !== undefined ? raw[col.label] : (raw[col.key] !== undefined ? raw[col.key] : '');
      const trimmed = typeof rawVal === 'string' ? rawVal.trim() : String(rawVal).trim();

      if (!trimmed) {
        // Empty cell — apply default or flag as required
        if (col.required) {
          errors[col.key] = t.errRequired;
          parsed[col.key] = '';
          return;
        }
        val = col.default !== undefined ? col.default : null;
      } else if (col.type === 'number') {
        const n = Number(trimmed);
        if (isNaN(n)) {
          errors[col.key] = t.errNotNumber;
          parsed[col.key] = trimmed;
          return;
        }
        val = n;
      } else if (col.type === 'boolean') {
        const lower = trimmed.toLowerCase();
        val = lower === 'true' || lower === '1' || lower === 'yes' || lower === 'sí';
      } else if (col.type === 'array') {
        val = trimmed.split(';').map(s => s.trim()).filter(Boolean);
      } else {
        val = trimmed;
      }

      // Enum check
      if (col.enum && val !== null && val !== '' && col.type !== 'array') {
        if (col.caseInsensitive) {
          const valLower = String(val).toLowerCase();
          const canonical = col.enum.find(e => String(e).toLowerCase() === valLower);
          if (canonical !== undefined) {
            parsed[col.key] = canonical;
          } else {
            warnings[col.key] = t.errInvalidEnum;
            parsed[col.key] = val;
          }
        } else {
          const allowed = col.enum.map(e => String(e));
          if (!allowed.includes(String(val))) {
            warnings[col.key] = t.errInvalidEnum;
          }
          parsed[col.key] = val;
        }
      } else {
        parsed[col.key] = val;
      }

      // IANA timezone check
      if (col.ianaTimezone && val !== null && val !== '') {
        try {
          const zones = Intl.supportedValuesOf('timeZone');
          if (!zones.includes(String(val))) {
            warnings[col.key] = t.warnInvalidTimezone;
          }
        } catch {
          // browser does not support Intl.supportedValuesOf — skip check
        }
      }
    });

    // Intra-batch duplicate detection
    let isDuplicate = false;
    if (dedupeKey && parsed[dedupeKey] !== undefined && parsed[dedupeKey] !== null && parsed[dedupeKey] !== '') {
      const dedupeVal = String(parsed[dedupeKey]).toLowerCase();
      if (seenDedupeValues.has(dedupeVal)) {
        warnings[dedupeKey] = t.warnDuplicate;
        isDuplicate = true;
      } else {
        seenDedupeValues.add(dedupeVal);
      }
    }

    return { parsed, errors, warnings, isDuplicate };
  });
}

// ── COMPONENT ─────────────────────────────────────────────────────────────────
//
// Two import modes are supported:
//
// - **Per-row mode** (preferred, required for §11 rule 38):
//   Caller provides `onImportRow(row)` and `onDeleteRow(rowResult)`.
//   ImportWizard iterates rows one at a time, maintains a checkpoint list of
//   successfully written records, and on failure presents the 3-option
//   ConfirmDialog from rule 38 (Retry, Rollback, Keep partial).
//
// - **Legacy batch mode** (back-compat):
//   Caller provides `onImport(rows)` alone. ImportWizard passes all valid
//   rows in a single call. On failure it surfaces the inline error banner
//   but cannot roll back (there is no per-row identifier). Existing callers
//   that write people.json as a single file stay on this path.
export default function ImportWizard({ lang, onClose, onImport, onImportRow, onDeleteRow, columns, templateFilename, title, dedupeKey, onDownloadTemplate }) {
  const t = T[lang] || T.en;
  const [step, setStep]           = useState(1);
  const [validated, setValidated] = useState([]);
  const [parseError, setParseError] = useState(null);
  const [importError, setImportError] = useState(null);
  const [importedCount, setImportedCount] = useState(0);
  const [importing, setImporting] = useState(false);
  // §11 rule 38 state. `failureState` holds the failing row + its position
  // in the queue + the checkpoint list of successfully written rows so the
  // rollback dialog can offer Retry / Rollback / Keep partial. The live
  // checkpoint itself is threaded as a local `written` array through
  // runPerRowImport — no component state needed for the counter because
  // the progress bar reads failureState.checkpointSoFar on failure.
  const [failureState, setFailureState] = useState(null);

  // ── Step 1: Upload ────────────────────────────────────────────────────────
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setParseError(null);
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const text = ev.target.result;
        const { rows } = parseCSV(text);
        if (rows.length === 0) { setParseError(t.emptyFile); return; }
        const result = validateRows(rows, columns, t, dedupeKey);
        setValidated(result);
        setStep(2);
      } catch {
        setParseError(t.parseError);
      }
    };
    reader.onerror = () => setParseError(t.parseError);
    reader.readAsText(file, 'UTF-8');
  };

  // ── Step 2: Preview ───────────────────────────────────────────────────────
  const validRows      = validated.filter(r => Object.keys(r.errors).length === 0);
  const importableRows = validRows.filter(r => !r.isDuplicate);
  const errorRows      = validated.filter(r => Object.keys(r.errors).length > 0);
  const warningRows    = validated.filter(r => Object.keys(r.warnings).length > 0 && Object.keys(r.errors).length === 0);

  // ── Per-row sequential writer with checkpoint and rule-38 rollback ───────
  // `startIndex` supports resume-after-retry without re-writing the earlier
  // rows that already landed successfully in the current session.
  const runPerRowImport = async (rows, startIndex = 0, priorCheckpoint = []) => {
    const written = [...priorCheckpoint];
    for (let i = startIndex; i < rows.length; i++) {
      const row = rows[i];
      try {
        const result = await onImportRow(row);
        if (result !== undefined && result !== null) written.push(result);
        // checkpoint tracked locally in `written`; no state update needed
        // during the happy path — we only need it when failure opens the dialog.
      } catch (err) {
        setFailureState({
          row,
          index: i,
          rows,
          checkpointSoFar: written,
          error: err && (err.message || String(err)),
        });
        setImporting(false);
        return;
      }
    }
    setImportedCount(written.length);
    setImporting(false);
    setStep(3);
  };

  const handleImport = async () => {
    setImportError(null);
    setImporting(true);
    setFailureState(null);
    const rows = importableRows.map(r => r.parsed);

    if (typeof onImportRow === 'function') {
      // Per-row mode — full §11 rule 38 checkpoint / rollback path.
      await runPerRowImport(rows, 0, []);
      return;
    }

    // Legacy batch mode — single onImport(rows) call, no rollback capability.
    try {
      const result = await onImport(rows);
      setImportedCount(typeof result === 'number' ? result : rows.length);
      setStep(3);
    } catch (err) {
      setImportError(err.message || String(err));
    } finally {
      setImporting(false);
    }
  };

  // ── §11 rule 38 — three-option rollback dialog handlers ─────────────────
  const handleFailureRetry = async () => {
    if (!failureState) return;
    const { rows, index, checkpointSoFar } = failureState;
    setFailureState(null);
    setImporting(true);
    await runPerRowImport(rows, index, checkpointSoFar);
  };

  const handleFailureRollback = async () => {
    if (!failureState) return;
    const toRollback = [...failureState.checkpointSoFar];
    setFailureState(null);
    setImporting(true);
    if (typeof onDeleteRow === 'function') {
      for (const rowResult of toRollback) {
        try { await onDeleteRow(rowResult); } catch { /* best-effort */ }
      }
    }
    setImportedCount(0);
    setImporting(false);
    onClose();
  };

  const handleFailureKeep = () => {
    if (!failureState) return;
    const kept = failureState.checkpointSoFar.length;
    setFailureState(null);
    setImportedCount(kept);
    setImporting(false);
    onClose();
  };

  // ── Shared styles ─────────────────────────────────────────────────────────
  const panelStyle = {
    background: SURFACE_WHITE,
    border: `1px solid ${BORDER_DEFAULT}`,
    borderRadius: 10,
    padding: '1.5rem',
  };

  const btnPrimary = {
    ...BTN_PRIMARY,
    ...FONT_BUTTON,
    border: 'none', borderRadius: 8,
    padding: '0.55rem 1.25rem', cursor: 'pointer',
  };

  const btnSecondary = {
    ...BTN_SECONDARY,
    ...FONT_BUTTON,
    fontWeight: 400,
    borderRadius: 8, padding: '0.55rem 1.25rem', cursor: 'pointer',
  };

  const titleStyle = {
    ...FONT_SECTION_TITLE,
    color: TEXT_HEADING, margin: '0 0 1.25rem',
  };

  // The §11 rule 38 failure dialog must be mounted on every step so it can
  // surface whenever `failureState` is set (import failure occurs on step 2).
  const failureDialog = (
    <ImportFailureDialog
      failureState={failureState}
      t={t}
      onRetry={handleFailureRetry}
      onRollback={handleFailureRollback}
      onKeep={handleFailureKeep}
      supportsRollback={typeof onDeleteRow === 'function'}
    />
  );

  // ── STEP 1 ────────────────────────────────────────────────────────────────
  if (step === 1) {
    return (
      <>
      <div style={panelStyle}>
        <h2 style={titleStyle}>{title[lang] || title.en}</h2>
        <p style={{ ...FONT_LABEL, color: TEXT_MUTED, margin: '0 0 0.5rem' }}>
          {t.step1Hint}
        </p>
        <p style={{ ...FONT_SMALL, color: TEXT_DISABLED, margin: '0 0 1.5rem' }}>
          {t.delimiterHint}
        </p>

        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
          <button
            onClick={() => onDownloadTemplate ? onDownloadTemplate() : generateTemplate(columns, templateFilename)}
            style={btnSecondary}
          >
            {t.downloadTemplate}
          </button>

          <label style={{
            ...btnPrimary,
            display: 'inline-flex', alignItems: 'center',
          }}>
            {t.uploadFile}
            <input
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
          </label>
        </div>

        {parseError && (
          <p style={{ ...FONT_LABEL, color: ERROR_TEXT, margin: '0 0 1rem' }}>
            {parseError}
          </p>
        )}

        <button onClick={onClose} style={btnSecondary}>
          {t.back}
        </button>
      </div>
      {failureDialog}
      </>
    );
  }

  // ── STEP 2 ────────────────────────────────────────────────────────────────
  if (step === 2) {
    return (
      <>
      <div style={panelStyle}>
        <h2 style={titleStyle}>{title[lang] || title.en}</h2>

        {/* Summary */}
        <div style={{ marginBottom: '1rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <span style={{ ...FONT_LABEL, color: BRAND_PRIMARY_DARK, background: STATUS_GOOD.bg, padding: '0.25rem 0.6rem', borderRadius: 5 }}>
            {t.summaryReady.replace('{n}', importableRows.length)}
          </span>
          {errorRows.length > 0 && (
            <span style={{ ...FONT_LABEL, color: ERROR_TEXT, background: ERROR_BG, padding: '0.25rem 0.6rem', borderRadius: 5 }}>
              {t.summaryErrors.replace('{n}', errorRows.length)}
            </span>
          )}
          {warningRows.length > 0 && (
            <span style={{ ...FONT_LABEL, color: STATUS_WARN.text, background: STATUS_WARN.bg, padding: '0.25rem 0.6rem', borderRadius: 5 }}>
              {t.summaryWarnings.replace('{n}', warningRows.length)}
            </span>
          )}
        </div>

        {/* Table */}
        <div style={{ overflowX: 'auto', borderRadius: 8, border: `1px solid ${BORDER_DEFAULT}`, marginBottom: '1.25rem', maxHeight: 400, overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', background: BG_SURFACE }}>
            <thead>
              <tr>
                <th style={thSt}>#</th>
                {columns.map(c => (
                  <th key={c.key} style={thSt}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {validated.map((row, idx) => {
                const hasError = Object.keys(row.errors).length > 0;
                return (
                  <tr key={idx} style={{ background: idx % 2 === 0 ? BG_SURFACE : SURFACE_WHITE }}>
                    <td style={{ ...tdSt, color: hasError ? ERROR_TEXT : TEXT_MUTED, fontWeight: 600 }}>
                      {idx + 1}
                    </td>
                    {columns.map(c => {
                      const err = row.errors[c.key];
                      const warn = row.warnings[c.key];
                      const cellBg = err ? ERROR_BG : warn ? STATUS_WARN.bg : 'transparent';
                      const val = row.parsed[c.key];
                      const display = val === null || val === undefined ? '' : Array.isArray(val) ? val.join('; ') : String(val);
                      return (
                        <td key={c.key} style={{ ...tdSt, background: cellBg }} title={err || warn || ''}>
                          {display}
                          {err && <span style={{ display: 'block', fontSize: '0.62rem', color: ERROR_TEXT }}>{err}</span>}
                          {warn && !err && <span style={{ display: 'block', fontSize: '0.62rem', color: STATUS_WARN.text }}>{warn}</span>}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {importError && (
          <p style={{ ...FONT_LABEL, color: ERROR_TEXT, margin: '0 0 1rem' }}>
            {t.errorImport}{importError}
          </p>
        )}

        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button
            onClick={() => { setStep(1); setValidated([]); setImportError(null); }}
            disabled={importing}
            style={{ ...btnSecondary, opacity: importing ? 0.5 : 1, cursor: importing ? 'not-allowed' : 'pointer' }}
          >
            {t.back}
          </button>
          <button
            onClick={handleImport}
            disabled={importableRows.length === 0 || importing}
            style={{
              ...btnPrimary,
              opacity: (importableRows.length === 0 || importing) ? 0.5 : 1,
              cursor: (importableRows.length === 0 || importing) ? 'not-allowed' : 'pointer',
            }}
          >
            {importing
              ? t.importing
              : importableRows.length === 0
                ? t.noValidRows
                : t.importN.replace('{n}', importableRows.length)}
          </button>
        </div>
      </div>
      {failureDialog}
      </>
    );
  }

  // ── STEP 3 ───────────────────────────────────────────────────────────────
  return (
    <>
      <div style={panelStyle}>
        <h2 style={titleStyle}>{title[lang] || title.en}</h2>
        <p style={{ ...FONT_BODY, color: BRAND_PRIMARY_DARK, margin: '0 0 1.5rem' }}>
          {t.successMsg.replace('{n}', importedCount)}
        </p>
        <button onClick={onClose} style={btnPrimary}>
          {t.done}
        </button>
      </div>
      {failureDialog}
    </>
  );
}

// §11 rule 38 — 3-option dialog displayed when a per-row write fails mid-batch.
// Mounted by the wizard on every step so it can be surfaced regardless of
// where in the flow failure occurs. `supportsRollback` is false when the
// caller did not provide an `onDeleteRow` callback; in that case the
// rollback path is unavailable (button hidden) and the user is effectively
// limited to Retry + Keep partial.
function ImportFailureDialog({ failureState, t, onRetry, onRollback, onKeep, supportsRollback }) {
  if (!failureState) return null;
  const writtenCount = failureState.checkpointSoFar.length;
  const message = t.failureDialogMsg.replace('{n}', writtenCount);
  return (
    <ConfirmDialog
      open
      message={message}
      onCancel={onKeep}
      onConfirm={supportsRollback ? onRollback : onKeep}
      cancelLabel={t.failureKeep}
      confirmLabel={supportsRollback ? t.failureRollback : t.failureKeep}
      confirmColor={supportsRollback ? BTN_DESTRUCTIVE_OUTLINE : BTN_SECONDARY}
      secondaryAction={{
        label: t.failureRetry,
        onClick: onRetry,
        color: BTN_PRIMARY,
      }}
    />
  );
}

// ── TABLE STYLES ──────────────────────────────────────────────────────────────
const thSt = {
  ...FONT_TINY,
  fontWeight: 600,
  color: TEXT_DISABLED,
  letterSpacing: '0.04em', textTransform: 'uppercase',
  padding: '0.4rem 0.6rem', borderBottom: `1px solid ${BORDER_DEFAULT}`,
  textAlign: 'left', background: BG_HOVER_LT,
  whiteSpace: 'nowrap', position: 'sticky', top: 0, zIndex: 1,
};

const tdSt = {
  ...FONT_SMALL,
  padding: '0.35rem 0.6rem', borderBottom: `1px solid ${BG_SHELL}`,
  color: TEXT_DEFAULT,
  verticalAlign: 'top', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis',
};
